package com.karyadina.ui.screens

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.karyadina.model.*
import com.karyadina.service.TaskService
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class TaskTrackerViewModel(
    private val taskService: TaskService
) : ViewModel() {

    private val _tasks = MutableStateFlow<List<Task>>(emptyList())
    private val _stats = MutableStateFlow(TaskStats())
    
    private val _filter = MutableStateFlow("Active") // Default filter to Active
    val filter = _filter.asStateFlow()
    
    private val _searchQuery = MutableStateFlow("")
    val searchQuery = _searchQuery.asStateFlow()

    private val _selectedTask = MutableStateFlow<Task?>(null)
    val selectedTask = _selectedTask.asStateFlow()

    val filteredTasks: StateFlow<List<Task>> = combine(
        _tasks,
        _filter,
        _searchQuery
    ) { tasks, filter, query ->
        if (query.isNotEmpty()) {
            taskService.searchTasks(query)
        } else {
            // Filter logic
            // We already loaded refined lists into _tasks? 
            // Better approach: ViewModel holds UI state, logic fetches correct list.
            // But if we want local filtering without DB hits every time, we should probably load all?
            // However, the Service APIs are specific. Let's rely on service.
            // The combine here is tricky if we want to change the SOURCE list.
            tasks // Just return tasks, because we will reload _tasks when filter changes
        }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val stats = _stats.asStateFlow()

    init {
        refreshTasks()
        
        // Observe storage refresh signals (e.g. from cloud sync)
        viewModelScope.launch {
            com.karyadina.service.AppStorage.refreshNotifier.collect {
                taskService.refreshFromStorage()
                refreshTasks()
            }
        }
    }

    fun setFilter(newFilter: String) {
        _filter.value = newFilter
        refreshTasks()
    }
    
    fun setSearchQuery(query: String) {
        _searchQuery.value = query
        // If query is present, we might want to trigger search
        refreshTasks()
    }

    fun selectTask(task: Task?) {
        _selectedTask.value = task
    }

    private fun refreshTasks() {
        // Update stats
        _stats.value = taskService.getStatistics()
        
        // Update tasks based on filter/query
        val query = _searchQuery.value
        _tasks.value = if (query.isNotBlank()) {
            taskService.searchTasks(query)
        } else {
            when (_filter.value) {
                "Active" -> taskService.getActiveTasks()
                "Today" -> taskService.getTodayTasks()
                "Overdue" -> taskService.getOverdueTasks()
                "Completed" -> taskService.getTasksByStatus(TaskStatus.COMPLETED)
                else -> taskService.getAllTasks()
            }
        }
    }

    fun addTask(task: Task) {
        taskService.createTask(task)
        refreshTasks()
    }

    fun updateTask(task: Task) {
        taskService.updateTask(task)
        refreshTasks()
        if (_selectedTask.value?.id == task.id) {
            _selectedTask.value = task
        }
    }

    fun toggleTaskCompletion(task: Task) {
        if (task.status == TaskStatus.COMPLETED.name) {
            taskService.updateTask(task.copy(
                status = TaskStatus.TODO.name,
                completedAt = null
            ))
        } else {
            taskService.completeTask(task.id)
        }
        refreshTasks()
        if (_selectedTask.value?.id == task.id) {
             // Refresh selected task if it's the one we just toggled
             _selectedTask.value = taskService.getTask(task.id)
        }
    }

    fun deleteTask(taskId: String) {
        taskService.deleteTask(taskId)
        if (_selectedTask.value?.id == taskId) {
            _selectedTask.value = null
        }
        refreshTasks()
    }
}
